import { API_ACCEPT, AuthApiPath } from "@/lib/auth/constants";
import newrelic from "@/lib/monitoring/newrelic";
import { buildApiEndpoint } from "@/lib/auth/buildApiEndpoint";

/* This is an edX Implementation of Django's CSRF protection */

export const CSRF_TOKEN_HEADER = "x-csrftoken";
export const CSRF_COOKIE_HEADER = "set-cookie";

type CsrfTokenResponse = {
  csrfToken: string;
  headers: Headers;
};

/**
 * Fetches the CSRF token from the LMS.
 * Returns the JSON response and the headers (for set-cookie).
 * Throws on network or unexpected error.
 */
async function getCsrfToken(): Promise<CsrfTokenResponse> {
  const url = buildApiEndpoint(AuthApiPath.CsrfToken);
  const headers = {
    accept: API_ACCEPT,
    "cache-control": "no-cache",
    pragma: "no-cache",
  } as Record<string, string>;

  try {
    const csrfRes = await fetch(url, {
      headers,
      body: null,
      credentials: "include",
      method: "GET",
    });
    const csrfData = await csrfRes.json();
    return { csrfToken: csrfData["csrfToken"], headers: csrfRes.headers };
  } catch (e) {
    newrelic.noticeError(e as Error);
    throw e;
  }
}

/**
 * Updates the provided headers by fetching a CSRF token and binding it to the headers.
 * This function retrieves the `csrfToken` and optionally a cookie from the backend, then adds them
 * to the input headers for subsequent requests.
 *
 * @param {Record<string, string>} inputHeaders - The headers to which the CSRF token and cookie will be added.
 * @returns {Promise<Record<string, string>>} A promise that resolves with the updated headers including:
 * - The CSRF token in the `x-csrftoken` header.
 * - The CSRF-related cookie in the `Cookie` header, if available.
 *
 * @throws Will throw an error if the CSRF token retrieval fails.
 *
 * @example
 * const headers = { Authorization: "Bearer token" };
 * const updatedHeaders = await requestAndBindCsrfToHeaders(headers);
 * console.log(updatedHeaders);
 * // Output:
 * // {
 * //   Authorization: "Bearer token",
 * //   "x-csrftoken": "retrievedCsrfToken",
 * //   Cookie: "csrfCookie=someValue", // If CSRF cookie is present
 * // }
 */
export async function requestAndBindCsrfToHeaders(
  inputHeaders: Record<string, string>,
) {
  const { csrfToken, headers: csrfHeaders } = await getCsrfToken();
  const cookie = csrfHeaders.get(CSRF_COOKIE_HEADER);
  return cookie
    ? { ...inputHeaders, Cookie: cookie, [CSRF_TOKEN_HEADER]: csrfToken }
    : { ...inputHeaders, [CSRF_TOKEN_HEADER]: csrfToken };
}
